import { slugify } from "@certn/utils";
import { css, cx } from "@emotion/css";
import { useEffect, useMemo, useState } from "react";
import { useFilter } from "react-aria";
import { Dialog, ListBoxItem, Key as AriaKey } from "react-aria-components";
import { useTranslation } from "react-i18next";

import { PickerFooter } from "./PickerFooter";
import { PickerListItem } from "./PickerListItem";
import { MediaQuery } from "../../../utils/MediaQuery";
import { Divider } from "../../divider/Divider";
import { Loader } from "../../loaders/loading/Loader";
import { DialogHeader } from "../../modal/DialogHeader";
import { Search } from "../../search/Search";
import { mq } from "../../utils";
import { PickerProps } from "../types";
import { PickerListBox } from "../PickerListBox";
import { PickerItem } from "../types";

interface Props<T> extends Omit<PickerProps<T>, "onDone"> {
  onClose: () => void;
  onDone?: (selected: AriaKey[]) => void;
  actionText?: string;
  hideClear?: boolean;
  truncateItem?: boolean;
  minimizePadding?: boolean;
}

function previouslySelectedKeys<T extends PickerItem>(
  previouslySelected: PickerProps<T>["selectedKeys"] | undefined
): AriaKey[] {
  if (!previouslySelected || previouslySelected === "all") {
    return [];
  }
  return [...previouslySelected];
}

export function selectionToKeySet<T extends PickerItem>(
  selection: PickerProps<T>["selectedKeys"],
  items: Iterable<T> | undefined,
  disabledKeys?: Iterable<AriaKey>,
  previouslySelected?: PickerProps<T>["selectedKeys"]
): Set<AriaKey> {
  if (selection === "all") {
    const disabled = new Set<AriaKey>(disabledKeys ?? []);
    const itemList = [...(items ?? [])];
    itemList.forEach((item) => {
      if (item.isDisabled) {
        disabled.add(item.id);
      }
    });
    const enabledVisible = itemList
      .filter((item) => !item.isDisabled && !disabled.has(item.id))
      .map((item) => item.id);
    const keptDisabled = previouslySelectedKeys(previouslySelected).filter(
      (key) => disabled.has(key)
    );
    return new Set([...keptDisabled, ...enabledVisible]);
  }
  return new Set(selection ?? []);
}

export function PickerDialog<T extends PickerItem>(props: Props<T>) {
  const { t } = useTranslation();

  const [search, setSearch] = useState("");
  const [selection, setSelection] = useState<PickerProps<T>["selectedKeys"]>(
    new Set([])
  );

  const {
    isLoading,
    label = t("Select an option"),
    showActionBar,
    testID = "picker",
    trigger,
    maxWidth,
    actionText,
    hideClear,
    onDone,
    truncateItem,
    minimizePadding,
    onSearch,
    ...listBoxProps
  } = props;

  const { contains } = useFilter({ sensitivity: "base" });

  const filteredItems = useMemo(() => {
    if (!search || !props.items || onSearch) {
      return props.items || [];
    }

    return [...props.items].filter((item) => {
      return (
        contains(item.name, search) ||
        Boolean(item.subtitle && contains(item.subtitle, search))
      );
    });
  }, [contains, props.items, search, onSearch]);

  useEffect(() => {
    if (props.selectedKeys) {
      setSelection(props.selectedKeys);
    }
  }, [props.selectedKeys]);

  const searchable =
    props.searchable || (props.items && [...props.items].length > 10);
  const clearable = selection && Array.from(selection).length > 0 && !hideClear;

  const resolveSelection = (next: PickerProps<T>["selectedKeys"]) =>
    selectionToKeySet(next, filteredItems, props.disabledKeys, selection);

  const handleDone = () => {
    const keys = resolveSelection(selection);
    if (onDone) {
      onDone(Array.from(keys));
      props.onClose?.();
      return;
    }

    props.onSelectionChange?.(keys);
    props.onClose?.();
  };

  return (
    <Dialog
      data-testid={`${testID}-dialog`}
      className={cx(styles.dialogHeight, "react-aria-Dialog")}
    >
      <MediaQuery sm>
        <DialogHeader
          onClose={props?.onClose}
          hideDivider
          testID={`${testID}-header`}
        >
          {label}
        </DialogHeader>

        {!searchable && <Divider />}
      </MediaQuery>
      {searchable && (
        <div className={styles.header}>
          <Search
            aria-label={t("Search")}
            name="search"
            onChange={(value) => {
              if (onSearch) {
                onSearch(value);
              }
              setSearch(value);
            }}
            placeholder={t("Search")}
            testID={`${testID}-search`}
            value={search}
            compact={false}
            maxWidth="unset"
          />
        </div>
      )}
      <PickerListBox
        aria-label={label}
        {...listBoxProps}
        selectedKeys={selection}
        onSelectionChange={(nextSelection) => {
          const keys = resolveSelection(nextSelection);
          if (showActionBar) {
            setSelection(keys);
            return;
          }

          props.onSelectionChange?.(keys);
          props.onClose?.();
        }}
        items={filteredItems}
        className={styles.pickerDialog}
      >
        {(item) => {
          return (
            <ListBoxItem
              id={item.id}
              textValue={item.name}
              className={styles.listBoxItem}
              isDisabled={item.isDisabled}
            >
              {(state) => {
                return (
                  <PickerListItem
                    isSelected={state.isSelected}
                    leading={item.leading}
                    selectionMode={state.selectionMode}
                    subtitle={item.subtitle}
                    testID={`${testID}-${slugify(item.name)}`}
                    title={item.name}
                    truncateTitle={truncateItem}
                    minimizePadding={minimizePadding}
                    isDisabled={item.isDisabled}
                  />
                );
              }}
            </ListBoxItem>
          );
        }}
      </PickerListBox>
      {showActionBar && (
        <PickerFooter
          onDone={handleDone}
          clearable={clearable}
          onClear={() => {
            setSelection(new Set([]));
            props.onSelectionChange?.(new Set([]));
            onSearch?.("");
            props.onClose?.();
          }}
          action={actionText ?? t("Done")}
          testID={`${testID}-footer`}
          isDisabled={Array.from(selection || []).length === 0}
        />
      )}
      {!showActionBar && (
        <MediaQuery sm>
          <PickerFooter
            onDone={props.onClose}
            action={t("Close")}
            testID={testID}
          />
        </MediaQuery>
      )}
      {props.isLoading && <Loader overlay background="surface-overlay" />}
    </Dialog>
  );
}

const styles = {
  header: css({
    borderBottomColor: "var(--border-base)",
    borderBottomStyle: "solid",
    borderBottomWidth: 1,
    paddingBottom: "var(--space-3)",
    paddingLeft: "var(--space-4)",
    paddingRight: "var(--space-4)",
    paddingTop: "var(--space-3)",
  }),
  listBoxItem: css({
    outline: "none",
    cursor: "pointer",
    borderRadius: "var(--border-radius-2)",
    "&[data-hovered]": {
      backgroundColor: "var(--background-ghost-hover)",
    },
    "&[data-focus-visible]": {
      boxShadow: "var(--focus-action)",
    },
    "&[data-disabled]": {
      cursor: "not-allowed",
      color: "var(--text-disabled)",
    },
    "&[data-disabled][data-hovered]": {
      backgroundColor: "transparent",
    },
  }),
  pickerDialog: css({ padding: "var(--space-1)", overflowY: "auto" }),
  dialogHeight: css(
    mq({
      maxHeight: [undefined, "400px"],
    })
  ),
};
